import { boundedMemo } from '../boundedMemo.js'
import { supabase } from '../supabase.js'
import { getMarketSession } from '../marketSession.js'
import { subscribeBrowserResume } from './browserResume.js'

const FIELDS = ['symbol', 'secType', 'exchange', 'currency', 'expiry', 'strike', 'right', 'multiplier']
const canonical = value => String(value ?? '').trim().toUpperCase()

export function normalizeQuoteContract(value = {}) {
  const request = {
    symbol: canonical(value.symbol),
    secType: canonical(value.secType || 'STK'),
    exchange: canonical(value.exchange || 'SMART'),
    currency: canonical(value.currency || 'USD'),
  }
  for (const field of FIELDS.slice(4)) {
    const normalized = canonical(value[field])
    if (normalized) request[field] = normalized
  }
  if (value.conid != null && value.conid !== '') {
    const conid = Number(value.conid)
    if (!Number.isInteger(conid) || conid <= 0) throw new TypeError('Quote contract conId must be a positive integer')
    request.conid = conid
  }
  if (!request.symbol) throw new TypeError('Quote contract symbol is required')
  const baseKey = FIELDS.map(field => request[field] || '').join('|')
  return { key: request.conid ? `${baseKey}|${request.conid}` : baseKey, request }
}

function positiveNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

// Position and P&L authority: actual Last during every trading session and
// the latest completed adjusted RTH session close while fully closed. Raw
// provider close, bid/ask, and mark remain available but never outrank those
// explicit authorities.
export function selectPositionQuotePrice(quote, marketSession, secType = quote?.contract?.secType) {
  const last = positiveNumber(quote?.last)
  const close = positiveNumber(quote?.close)
  const lastRth = positiveNumber(quote?.lastRth)
  const sessionClose = positiveNumber(quote?.sessionClose)
  const bid = positiveNumber(quote?.bid)
  const ask = positiveNumber(quote?.ask)
  const effectiveSession = classifyContractSession(quote?.contract) || marketSession
  const active = effectiveSession !== 'closed'
  if (String(secType || '').toUpperCase() === 'OPT' && active && bid != null && ask != null) {
    return (bid + ask) / 2
  }
  return effectiveSession === 'closed'
    ? (sessionClose ?? lastRth ?? last ?? close)
    : (last ?? sessionClose ?? lastRth ?? close)
}

const defaultEventSourceFactory = url => new EventSource(url)

export async function openIbkrEphemeralQuoteBatch({
  contracts,
  onQuote,
  onError = () => {},
  fetchImpl = (...args) => fetch(...args),
  eventSourceFactory = defaultEventSourceFactory,
} = {}) {
  const requests = (contracts || []).map(value => normalizeQuoteContract(value).request)
  if (requests.length === 0) return { release: async () => {} }
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Authenticated Erevis session is required for market data')
  const authorization = `Bearer ${session.access_token}`
  const response = await fetchImpl('/api/ibkr/market-data/subscriptions', {
    method: 'POST',
    headers: { Authorization: authorization, 'Content-Type': 'application/json' },
    body: JSON.stringify({ contracts: requests, requireAll: true }),
  })
  if (!response.ok) throw new Error(`Market-data subscription failed: ${response.status}`)
  const resource = await response.json()
  if (!resource.streamUrl || !resource.releaseToken) {
    throw new Error('Market-data subscription did not return a releasable stream')
  }

  let active = true
  let releasePromise = null
  const publish = quote => {
    if (!active || !quote || typeof quote !== 'object') return
    try { onQuote?.(quote) } catch { /* isolate the visible chain */ }
  }
  for (const quote of resource.snapshots || []) publish(quote)
  const source = eventSourceFactory(resource.streamUrl)
  const receive = event => {
    try { publish(JSON.parse(event.data)) } catch { /* malformed SSE */ }
  }
  source.addEventListener('message', receive)
  source.addEventListener('quote', receive)
  source.addEventListener('error', () => {
    if (!active) return
    try { onError(new Error('Market-data stream disconnected')) } catch { /* observer only */ }
  })

  return {
    release() {
      if (releasePromise) return releasePromise
      active = false
      source.close()
      releasePromise = fetchImpl('/api/ibkr/market-data/subscriptions', {
        method: 'DELETE',
        headers: { Authorization: authorization, 'Content-Type': 'application/json' },
        body: JSON.stringify({ releaseToken: resource.releaseToken }),
        keepalive: true,
      }).then(response => {
        if (!response.ok) throw new Error(`Market-data release failed: ${response.status}`)
      }).catch(error => {
        try { onError(error) } catch { /* observer only */ }
      })
      return releasePromise
    },
  }
}

export function createIbkrQuoteSubscriptionStore({
  fetchImpl = (...args) => fetch(...args),
  eventSourceFactory = defaultEventSourceFactory,
  debounceMs = 10,
  unsubscribeGraceMs = 30_000,
  recoveryDelaysMs = [1_000, 2_000, 5_000],
  onError = () => {},
} = {}) {
  const entries = new Map()
  const resolvedKeys = new Map()
  const recoveryDelays = recoveryDelaysMs.length > 0 ? recoveryDelaysMs : [5_000]
  const sources = new Set()
  let rebuildTimer = null
  let generation = 0
  let recoveryAttempt = 0

  function report(error) {
    try { onError(error instanceof Error ? error : new Error(String(error))) } catch { /* observer only */ }
  }

  function closeSources() {
    for (const source of sources) source.close()
    sources.clear()
    resolvedKeys.clear()
  }

  function findEntry(quote) {
    const resolved = resolvedKeys.get(String(quote?.contractKey || ''))
    if (resolved) return entries.get(resolved)
    if (!quote?.contract) return null
    try {
      const exact = entries.get(normalizeQuoteContract(quote.contract).key)
      if (exact) return exact
    } catch { /* malformed provider payload */ }
    const symbol = canonical(quote.contract.symbol)
    const secType = canonical(quote.contract.secType || 'STK')
    const exchange = canonical(quote.contract.exchange || 'SMART')
    return [...entries.values()].find(item => (
      item.contract.symbol === symbol
      && item.contract.secType === secType
      && item.contract.exchange === exchange
    )) || null
  }

  function publish(quote, { force = false } = {}) {
    if (!quote || typeof quote !== 'object') return
    const entry = findEntry(quote)
    if (!entry) return
    const sequence = Number(quote.sequence)
    if (!Number.isFinite(sequence) || (!force && sequence <= entry.sequence)) return
    entry.sequence = sequence
    entry.lastQuote = quote
    if (quote.contractKey) resolvedKeys.set(String(quote.contractKey), entry.key)
    for (const listener of entry.listeners) {
      try { listener(quote) } catch { /* isolate consumers */ }
    }
  }

  function publishStaleSnapshots() {
    for (const entry of entries.values()) {
      if (!entry.lastQuote) continue
      const stale = { ...entry.lastQuote, freshness: 'stale', freshnessReason: null }
      entry.lastQuote = stale
      for (const listener of entry.listeners) {
        try { listener(stale) } catch { /* isolate consumers */ }
      }
    }
  }

  function scheduleRecovery() {
    if (entries.size === 0) return
    generation += 1
    const expectedGeneration = generation
    const delay = recoveryDelays[Math.min(recoveryAttempt, recoveryDelays.length - 1)]
    recoveryAttempt += 1
    if (rebuildTimer) clearTimeout(rebuildTimer)
    rebuildTimer = setTimeout(() => {
      rebuildTimer = null
      void rebuild(expectedGeneration)
    }, delay)
  }

  async function rebuild(expectedGeneration) {
    const currentEntries = [...entries.values()]
    if (currentEntries.length === 0) {
      if (expectedGeneration === generation) closeSources()
      return
    }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Authenticated Erevis session is required for market data')
      const requestResource = async selectedEntries => {
        const response = await fetchImpl('/api/ibkr/market-data/subscriptions', {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ contracts: selectedEntries.map(item => item.contract) }),
        })
        if (!response.ok) throw new Error(`Market-data subscription failed: ${response.status}`)
        const resource = await response.json()
        if (!resource.streamUrl) throw new Error('Market-data subscription did not return a stream URL')
        return resource
      }
      let isolationRequests = 0
      const resolveKnownHealthyResources = async selectedEntries => {
        if (selectedEntries.length === 0) return { resources: [], failures: [] }
        try {
          isolationRequests += 1
          return { resources: [await requestResource(selectedEntries)], failures: [] }
        } catch (error) {
          if (selectedEntries.length === 1 || isolationRequests >= 16) {
            return { resources: [], failures: [error] }
          }
          const middle = Math.ceil(selectedEntries.length / 2)
          const left = await resolveKnownHealthyResources(selectedEntries.slice(0, middle))
          const right = await resolveKnownHealthyResources(selectedEntries.slice(middle))
          return {
            resources: [...left.resources, ...right.resources],
            failures: [...left.failures, ...right.failures],
          }
        }
      }
      let resources
      let failures
      try {
        resources = [await requestResource(currentEntries)]
        failures = []
      } catch (batchError) {
        if (currentEntries.length === 1) throw batchError
        // A server outage and one bad contract both arrive through the browser
        // proxy as a generic failure. Prove the provider is healthy with at
        // most two singleton probes before splitting the rejected registry;
        // this prevents an outage from fanning out into one request per asset.
        const probeCandidates = [
          ...currentEntries.filter(entry => entry.lastQuote),
          ...currentEntries.filter(entry => !entry.lastQuote),
        ].slice(0, 2)
        let probe = null
        let probeEntry = null
        for (const entry of probeCandidates) {
          try {
            probe = await requestResource([entry])
            probeEntry = entry
            break
          } catch { /* try one more known registry member */ }
        }
        if (!probe || !probeEntry) throw batchError
        const isolated = await resolveKnownHealthyResources(
          currentEntries.filter(entry => entry !== probeEntry),
        )
        resources = [probe, ...isolated.resources]
        failures = isolated.failures
      }
      if (expectedGeneration !== generation) return
      if (resources.length === 0) throw failures[0] || new Error('Market-data subscription failed')

      closeSources()
      for (const resource of resources) {
        for (const quote of resource.snapshots || []) publish(quote, { force: true })
        const connectedSource = eventSourceFactory(resource.streamUrl)
        sources.add(connectedSource)
        const receive = (event) => {
          if (!sources.has(connectedSource)) return
          try { publish(JSON.parse(event.data)) } catch { /* malformed SSE */ }
        }
        connectedSource.addEventListener('message', receive)
        connectedSource.addEventListener('quote', receive)
        connectedSource.addEventListener('error', () => {
          if (!sources.has(connectedSource)) return
          report(new Error('Market-data stream disconnected'))
          publishStaleSnapshots()
          closeSources()
          scheduleRecovery()
        })
      }
      for (const failure of failures) report(failure)
      recoveryAttempt = 0
    } catch (error) {
      if (expectedGeneration === generation) {
        report(error)
        scheduleRecovery()
      }
    }
  }

  function scheduleRebuild() {
    recoveryAttempt = 0
    generation += 1
    const expectedGeneration = generation
    if (rebuildTimer) clearTimeout(rebuildTimer)
    rebuildTimer = setTimeout(() => {
      rebuildTimer = null
      void rebuild(expectedGeneration)
    }, debounceMs)
  }

  function subscribe(contractValue, listener) {
    const normalized = normalizeQuoteContract(contractValue)
    let entry = entries.get(normalized.key)
    const isNew = !entry
    if (!entry) {
      entry = { key: normalized.key, contract: normalized.request, listeners: new Set(), releaseTimer: null, sequence: -1, lastQuote: null }
      entries.set(entry.key, entry)
    }
    if (entry.releaseTimer) {
      clearTimeout(entry.releaseTimer)
      entry.releaseTimer = null
    }
    entry.listeners.add(listener)
    if (entry.lastQuote) listener(entry.lastQuote)
    if (isNew) scheduleRebuild()

    let released = false
    return ({ immediate = false } = {}) => {
      if (released) return
      released = true
      entry.listeners.delete(listener)
      if (entry.listeners.size > 0 || entry.releaseTimer) return
      if (immediate) {
        entries.delete(entry.key)
        generation += 1
        if (rebuildTimer) clearTimeout(rebuildTimer)
        rebuildTimer = null
        if (entries.size === 0) closeSources()
        else scheduleRebuild()
        return
      }
      entry.releaseTimer = setTimeout(() => {
        entry.releaseTimer = null
        if (entry.listeners.size > 0) return
        entries.delete(entry.key)
        if (entries.size === 0) {
          generation += 1
          if (rebuildTimer) clearTimeout(rebuildTimer)
          rebuildTimer = null
          closeSources()
        } else {
          scheduleRebuild()
        }
      }, unsubscribeGraceMs)
    }
  }

  return { subscribe }
}

export const ibkrQuoteSubscriptionStore = createIbkrQuoteSubscriptionStore()

const easternClock = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})
const WEEKDAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

const overnightForMinute = boundedMemo(minute => {
  const parts = Object.fromEntries(
    easternClock.formatToParts(new Date(minute * 60_000))
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value]),
  )
  const weekday = WEEKDAY[parts.weekday]
  const minutes = Number(parts.hour) * 60 + Number(parts.minute)
  if (weekday === 0) return minutes >= 20 * 60
  if (weekday >= 1 && weekday <= 4) return minutes < 3 * 60 + 50 || minutes >= 20 * 60
  if (weekday === 5) return minutes < 3 * 60 + 50
  return false
}, 256)

export function isIbkrOvernightSession(epochMs = Date.now()) {
  return overnightForMinute(Math.floor(new Date(epochMs).getTime() / 60_000))
}

const contractClockFormatters = new Map()

function contractLocalMinute(epochMs, timezone) {
  let localMinute = contractClockFormatters.get(timezone)
  if (!localMinute) {
    let formatter
    try {
      formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      })
    } catch {
      return null
    }
    localMinute = boundedMemo(minute => {
      const parts = Object.fromEntries(
        formatter.formatToParts(new Date(minute * 60_000))
          .filter(part => part.type !== 'literal')
          .map(part => [part.type, Number(part.value)]),
      )
      const day = Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / 86_400_000)
      return day * 1440 + parts.hour * 60 + parts.minute
    }, 64)
    if (contractClockFormatters.size >= 32) contractClockFormatters.delete(contractClockFormatters.keys().next().value)
    contractClockFormatters.set(timezone, localMinute)
  }
  return localMinute(Math.floor(new Date(epochMs).getTime() / 60_000))
}

const parseScheduleRanges = boundedMemo(value => {
  const ranges = []
  const coveredDays = []
  const dayNumber = token => Math.floor(Date.UTC(
    Number(token.slice(0, 4)), Number(token.slice(4, 6)) - 1, Number(token.slice(6, 8)),
  ) / 86_400_000)
  for (const rawSegment of String(value || '').split(';')) {
    const segment = rawSegment.trim()
    if (!segment) continue
    const colon = segment.indexOf(':')
    if (colon !== 8 || !/^\d{8}$/.test(segment.slice(0, colon))) continue
    const date = segment.slice(0, colon)
    const span = segment.slice(colon + 1)
    if (span === 'CLOSED') {
      coveredDays.push([dayNumber(date), dayNumber(date)])
      continue
    }
    const dash = span.indexOf('-')
    if (dash < 0) continue
    const startClock = span.slice(0, dash)
    const endToken = span.slice(dash + 1)
    if (!/^\d{4}$/.test(startClock)) continue
    const endDate = endToken.includes(':') ? endToken.slice(0, 8) : date
    const endClock = endToken.includes(':') ? endToken.slice(9) : endToken
    if (!/^\d{8}$/.test(endDate) || !/^\d{4}$/.test(endClock)) continue
    const minute = (dayToken, clockToken) => {
      const day = dayNumber(dayToken)
      return day * 1440 + Number(clockToken.slice(0, 2)) * 60 + Number(clockToken.slice(2, 4))
    }
    const start = minute(date, startClock)
    let end = minute(endDate, endClock)
    if (end <= start) end += 1440
    ranges.push([start, end])
    coveredDays.push([Math.floor(start / 1440), Math.floor(end / 1440)])
  }
  return { ranges, coveredDays }
}, 128)

function scheduleRanges(value) {
  return parseScheduleRanges(String(value || ''))
}

export function classifyContractSession(contract, epochMs = Date.now()) {
  const timezone = String(contract?.timezone || contract?.timeZoneId || '').trim()
  if (!timezone) return null
  const current = contractLocalMinute(epochMs, timezone)
  if (current == null) return null
  const trading = scheduleRanges(contract?.tradingHours)
  const liquid = scheduleRanges(contract?.liquidHours)
  if (liquid.ranges.some(([start, end]) => start <= current && current < end)) return 'regular'
  if (trading.ranges.some(([start, end]) => start <= current && current < end)) return 'extended'
  // ContractDetails schedules are dated snapshots, not perpetual calendars.
  // A durable quote can outlive their coverage while Last keeps advancing.
  // Missing dates must fall back to the caller's session, never pin prices to
  // an old RTH close as if the provider had explicitly declared today CLOSED.
  const currentDay = Math.floor(current / 1440)
  const coversToday = [...trading.coveredDays, ...liquid.coveredDays]
    .some(([start, end]) => start <= currentDay && currentDay <= end)
  if (!coversToday) return null
  return 'closed'
}

export function resolveQuoteDisplaySession(snapshot, epochMs = Date.now()) {
  if (!snapshot) return null
  const secType = canonical(snapshot.contract?.secType || 'STK')
  if (secType === 'STK') {
    if (isIbkrOvernightSession(epochMs)) return 'overnight'
    return getMarketSession(new Date(epochMs))
  }
  return classifyContractSession(snapshot.contract, epochMs) || snapshot.session || null
}

function equityQuoteDisplaySession(epochMs) {
  if (isIbkrOvernightSession(epochMs)) return 'overnight'
  return getMarketSession(new Date(epochMs))
}

let nextBoundaryWindow = null

/**
 * Return the exact next minute boundary at which a US equity quote's display
 * session changes. Session rules are evaluated in New York time, while the
 * returned value is an absolute epoch so every browser timezone transitions
 * on the same instant.
 */
export function nextEquityQuoteSessionBoundary(epochMs = Date.now()) {
  const now = Number(epochMs)
  if (!Number.isFinite(now)) return null
  if (nextBoundaryWindow && now >= nextBoundaryWindow.from && now < nextBoundaryWindow.until) return nextBoundaryWindow.until
  const currentSession = equityQuoteDisplaySession(now)
  const firstMinute = Math.floor(now / 60_000) * 60_000 + 60_000
  const searchLimit = firstMinute + (8 * 24 * 60 * 60_000)

  for (let candidate = firstMinute; candidate <= searchLimit; candidate += 60_000) {
    if (equityQuoteDisplaySession(candidate) !== currentSession) {
      nextBoundaryWindow = { from: now, until: candidate }
      return candidate
    }
  }
  return null
}

export function normalizeQuoteSnapshot(quote, authority) {
  const receivedTs = Number(quote.serviceReceiptTime) || Date.now()
  const secType = canonical(quote?.contract?.secType || 'STK')
  const scheduledSession = classifyContractSession(quote?.contract, receivedTs)
  const assetSession = authority === 'OVERNIGHT'
    ? 'overnight'
    : (scheduledSession || (secType === 'FUT' || secType === 'OPT' ? quote.session : null))
  const freshness = assetSession === 'closed' && quote.freshness === 'live' ? 'stale' : quote.freshness
  // Presentation provenance only: keep existing freshness/isLive semantics.
  const freshnessReason = assetSession === 'closed' && quote.freshness === 'live'
    && Number(quote.marketDataType) === 1 ? 'market-closed' : null
  const isLive = freshness === 'live' && Number(quote.marketDataType) === 1 && assetSession !== 'closed'
  return Object.freeze({
    ...quote,
    type: 'quote',
    ts: receivedTs,
    receivedTs,
    eventTs: quote.providerEventTime ?? null,
    source: quote.source || 'ibkr-market-data-service',
    freshness,
    freshnessReason,
    isLive,
    session: assetSession,
    authority,
  })
}

function emitQuoteFields(listener, snapshot) {
  for (const field of ['last', 'close', 'sessionClose', 'bid', 'ask', 'open', 'high', 'low', 'volume', 'mark', 'lastRth']) {
    const value = Number(snapshot[field])
    if (!Number.isFinite(value)) continue
    listener({
      type: 'tick',
      field,
      value,
      ts: snapshot.receivedTs,
      receivedTs: snapshot.receivedTs,
      eventTs: snapshot.eventTs,
      source: snapshot.source,
      sequence: snapshot.sequence,
      freshness: snapshot.freshness,
      freshnessReason: snapshot.freshnessReason,
      isLive: snapshot.isLive,
      session: snapshot.session,
      marketDataType: snapshot.marketDataType,
      contract: snapshot.contract,
      authority: snapshot.authority,
    })
  }
}

export function createIbkrQuoteStream({
  store = ibkrQuoteSubscriptionStore,
  now = () => Date.now(),
  setTimeoutImpl = (...args) => setTimeout(...args),
  clearTimeoutImpl = handle => clearTimeout(handle),
  subscribeResume = subscribeBrowserResume,
} = {}) {
  function subscribeWithAuthority(symbol, params, onQuote, onReset) {
    const secType = canonical(params.secType || 'STK')
    const dynamicOvernight = secType === 'STK' && !params.exchange
    let authority = dynamicOvernight && isIbkrOvernightSession(now()) ? 'OVERNIGHT' : canonical(params.exchange || 'SMART')
    const subscribeAuthority = selectedAuthority => store.subscribe(
      { symbol, ...params, exchange: selectedAuthority },
      quote => onQuote(normalizeQuoteSnapshot(quote, selectedAuthority)),
    )
    let release = subscribeAuthority(authority)
    let policyTimer = null
    let active = true
    const scheduleAuthorityBoundary = () => {
      if (!dynamicOvernight || !active) return
      const currentTime = now()
      const boundary = nextEquityQuoteSessionBoundary(currentTime)
      if (boundary == null) return
      policyTimer = setTimeoutImpl(applyAuthorityBoundary, Math.max(0, boundary - currentTime))
    }
    const applyAuthorityBoundary = () => {
      if (!active) return
      if (policyTimer != null) clearTimeoutImpl(policyTimer)
      policyTimer = null
      const nextAuthority = isIbkrOvernightSession(now()) ? 'OVERNIGHT' : 'SMART'
      if (nextAuthority !== authority) {
        release?.({ immediate: true })
        onReset(nextAuthority)
        authority = nextAuthority
        release = subscribeAuthority(authority)
      }
      scheduleAuthorityBoundary()
    }
    scheduleAuthorityBoundary()
    const stopResume = dynamicOvernight ? subscribeResume(applyAuthorityBoundary) : null

    return () => {
      active = false
      stopResume?.()
      if (policyTimer != null) clearTimeoutImpl(policyTimer)
      release?.()
    }
  }

  return {
    async subscribe(symbol, listener, params = {}) {
      return subscribeWithAuthority(
        symbol,
        params,
        snapshot => emitQuoteFields(listener, snapshot),
        authority => listener({ type: 'reset', field: 'reset', value: null, authority }),
      )
    },

    subscribeSnapshot(symbol, listener, params = {}) {
      // Keep the last complete snapshot visible during the brief SMART ↔
      // OVERNIGHT handoff. Publishing an empty state here would make UI
      // consumers fall back independently before the new venue responds.
      return subscribeWithAuthority(symbol, params, listener, () => {})
    },
  }
}

// Compatibility-shaped adapter for position surfaces. It deliberately lives
// on the non-chart resource; chart candles continue to use ibkrStream.
export const ibkrQuoteStream = createIbkrQuoteStream()
